import { Chip, LinearProgress } from "@mui/material";
import AedasLoading from "../../Shared/AedasLoading/AedasLoading";
import { ClassificationOption, confidOptions, levelOptions } from "../../../Utils/classificationOptions";
import DetailView, {
  ChipColor,
  DetailCard as Section,
  DetailField as Field,
  emailValue,
  ExpirationChip,
  formatDate,
  isEmpty,
  linkValue,
  text,
} from "../../Shared/DetailView/DetailView";
import { useCatalogs } from "../../../Hooks/useCatalogs";

// Las listas auxiliares salen de la caché compartida (useCatalogs): solo se
// piden al backend la primera vez, no cada vez que se abre una ficha
function InventoryElementView(props: any) {
  const { access, host, partner, licence, loading } = useCatalogs(["access", "host", "partner", "licence"]);

  if (loading) {
    return (
      <div className="w-100 h-100 d-flex justify-content-center">
        <AedasLoading></AedasLoading>
      </div>
    );
  }

  return (
    <div>
      <InventoryElementViewInner
        {...props}
        partner={partner ?? []}
        host={host ?? []}
        access={access ?? []}
        licence={licence ?? []}
        itemType={props.itemType}
      />
    </div>
  );
}

export default InventoryElementView;


const levelColors: ChipColor[] = ["success", "warning", "error"];
const confidColors: ChipColor[] = ["success", "info", "warning", "error", "error"];

const levelChip = (value: any, options: ClassificationOption[], colors: ChipColor[]) => {
  const option = isEmpty(value) ? undefined : options.find((o) => o.id === Number(value));
  if (!option) return null;
  return <Chip size="small" label={option.level} color={colors[option.id]} />;
};

function InventoryElementViewInner(props: any) {
  const { partner, host, access, licence, itemType } = props;
  const row = props.selectedRow;
  const isLicence = itemType === "licencia";

  const nameOf = (list: any[], id: any) => list.find((item: any) => item.id === id)?.name;

  // El formulario guarda el estado en "status"; "active" se mantiene por compatibilidad
  const isActive = row.status ?? row.active;

  const total = Number(row.accesses_number);
  const used = Number(row.consumed_users);
  const hasUsage = !isEmpty(row.accesses_number) && total > 0 && !isEmpty(row.consumed_users);

  return (
    <DetailView
      title={row.app_name}
      description={row.description}
      comment={row.comments}
      badges={
        <>
          {isLicence ? (
            <Chip
              size="small"
              color={isActive ? "success" : "default"}
              label={isActive ? "Activa" : "Inactiva"}
            />
          ) : null}
          <ExpirationChip date={row.expiration_day} adviseDays={row.advise_days} />
        </>
      }
    >
        <Section title="General">
          <Field label="Web">
            {linkValue(row.website)}
          </Field>
          <Field label="Email">
            {emailValue(row.email)}
          </Field>
          <Field label="Tipo de acceso">{nameOf(access, row.id_access)}</Field>
          <Field label="Alojamiento">{nameOf(host, row.id_host)}</Field>
        </Section>

        <Section title={isLicence ? "Licencia y accesos" : "Accesos"}>
          {isLicence ? (
            <>
              <Field label="Tipo de licencia">{nameOf(licence, row.id_licence)}</Field>
              <Field label="Nº de licencia">{text(row.licence_number)}</Field>
            </>
          ) : null}
          <Field label="Accesos">
            {hasUsage ? (
              <div className="detailUsage">
                <span>
                  <strong>{used}</strong> de {total}
                </span>
                <LinearProgress
                  variant="determinate"
                  value={Math.min(100, (used / total) * 100)}
                  color={used >= total ? "error" : "secondary"}
                />
              </div>
            ) : isEmpty(row.accesses_number) ? null : (
              `${row.accesses_number} disponibles`
            )}
          </Field>
        </Section>

        <Section title="Clasificación de seguridad">
          <Field label="Confidencialidad">
            {levelChip(row.confid, confidOptions, confidColors)}
          </Field>
          <Field label="Criticidad">{levelChip(row.crits, levelOptions, levelColors)}</Field>
          <Field label="Integridad">{levelChip(row.integrity, levelOptions, levelColors)}</Field>
          <Field label="Disponibilidad">{levelChip(row.dispo, levelOptions, levelColors)}</Field>
        </Section>

        <Section title="Proveedor">
          <Field label="Partner">{nameOf(partner, row.id_partner)}</Field>
          <Field label="Fabricante">{nameOf(partner, row.maker)}</Field>
          <Field label="Nº de proveedor">{text(row.supplier)}</Field>
        </Section>

        {/* La fecha de vencimiento ya aparece en el chip de la cabecera */}
        <Section title="Fechas y presupuesto">
          <Field label="Fecha de compra">{formatDate(row.purchase_date)}</Field>
          <Field label="Aviso de caducidad">
            {isEmpty(row.advise_days) || Number(row.advise_days) === 0
              ? null
              : `${row.advise_days} días antes`}
          </Field>
          <Field label="Partida presupuestaria">{text(row.budget)}</Field>
        </Section>

        <Section title="Responsables">
          <Field label="Dpto. de negocio">{text(row.departmen)}</Field>
          <Field label="Product Owner">{text(row.product_own)}</Field>
          <Field label="Business Owner">{text(row.buss_own)}</Field>
        </Section>
    </DetailView>
  );
}
